import gradientBackdrop from '@/assets/images/power-dialer-gradient.png';
import { Icon, Icons } from '@/components/icon';
import { Button } from '@/components/ui/design-system';
import { useTranslation } from '@/hooks/use-translation';
import { NoVonageSessionError, NoVonageTokenError } from '@/lib/authentication';
import {
  getLastChunkLoadError,
  isChunkLoadRecoveryPending,
  recoverFromChunkLoadError,
} from '@/lib/chunk-load-recovery';
import { isChunkLoadError } from '@/lib/error-classification';
import { getFirstNameSync } from '@/lib/user-greeting';
import { cn } from '@/lib/utils';
import { ANALYTICS_EVENTS } from '@/services/analytics';
import { useAnalytics } from '@/services/analytics/use-analytics';
import { STATUS_PAGE_PUBLIC_URL } from '@/services/status-page';
import { useQueryClient } from '@tanstack/react-query';
import { memo, useEffect, useRef, useState } from 'react';
import { AlloLogo } from '../logos/allo-logo';
import { reportSplashError } from './splash-error-telemetry';

type Props = {
  error?: Error | boolean;
  noTimeout?: boolean;
  /** First name shown in the greeting copy. When omitted, the `<Greeting>`
   *  child resolves it once from the React Query cache (with a `localStorage`
   *  fallback). Snapshotted at mount so the copy never pops mid-boot. */
  userName?: string;
  /** Discriminates the error framing.
   *  - `server` (default): "We can't reach our servers" — implies a backend
   *    reachability problem. Future status-page link surfaces here.
   *  - `client`: a runtime error inside the app itself. Same canvas, neutral
   *    copy, no status-page CTA — pointing the user at the public status page
   *    on a render bug would mislead. */
  kind?: 'server' | 'client';
};

const HIGHLIGHT_GRADIENT = 'var(--color-gradient-highlight)';
const DANGER_GRADIENT =
  'linear-gradient(90deg, var(--color-status-danger-dark) 0%, var(--color-status-danger) 100%)';
const WARNING_GRADIENT =
  'linear-gradient(90deg, var(--color-status-warning-dark) 0%, var(--color-status-warning) 100%)';

const LOADING_DURATION_MS = 10_000;

/**
 * Welcome line shown above the loading bar. Isolated so the `useQueryClient`
 * call only happens when the splash actually mounts in non-error mode — when
 * <Splashscreen> is used as a Sentry / router error fallback, the parent
 * <QueryClientProvider> isn't in scope, and calling `useQueryClient` there
 * would crash the fallback itself.
 *
 * The resolved name is snapshotted via lazy `useState` initialiser so the
 * copy can never pop from generic → personalized mid-crossfade.
 */
const Greeting = ({ userName }: { userName?: string }) => {
  const { t } = useTranslation('desktop_common');
  const queryClient = useQueryClient();
  const [resolvedName] = useState(() => userName ?? getFirstNameSync(queryClient));

  return (
    <p className="w-full text-balance text-center text-heading-16 text-primary-100">
      {resolvedName
        ? t('splashscreen.welcomeWithName', { name: resolvedName })
        : t('splashscreen.welcome')}
    </p>
  );
};

export const Splashscreen = memo(
  ({ error = false, noTimeout = false, userName, kind = 'server' }: Props) => {
    const timeoutMessageRef = useRef<HTMLDivElement>(null);
    const { t } = useTranslation('desktop_common');
    const { track } = useAnalytics();

    // Snapshot whether the splash mounted in error mode. Used to skip mounting
    // <Greeting> entirely on a Sentry / router error boundary fallback where no
    // QueryClientProvider sits above us. During a normal load → error
    // transition the greeting stays mounted and crossfades out via opacity.
    const [bootedInError] = useState(() => Boolean(error));

    // React-driven bar progress + state. Replaces the previous Web Animations
    // API approach because that locked the inline `width` to an animated value,
    // making a smooth transition into the error state impossible (the WAAPI
    // animation would keep pushing toward 100% while CSS tried to snap to 90%).
    // Now both states share a single fill <div> whose `width` + `background`
    // crossfade via CSS transitions when `error` flips.
    const [hasMounted, setHasMounted] = useState(false);
    useEffect(() => {
      // Defer to the next frame so the initial 0%→target transition kicks in.
      const id = requestAnimationFrame(() => setHasMounted(true));
      return () => cancelAnimationFrame(id);
    }, []);

    // Retry is a direct reload — no synthetic transition. We tried fading the
    // error block out before reloading, but interrupting a 500ms transition
    // mid-flight with a reload always reads as a jump. The browser's own
    // unload/repaint plus the button's press state give enough feedback that
    // the click registered. Chunk-load errors evict the failed asset from the
    // HTTP cache first — a plain reload would re-serve the cached bad
    // response and re-fail. The boundary may receive a secondary error
    // instead of the chunk failure itself, so fall back to the last one the
    // preload handler recorded.
    const handleRetry = () => {
      const chunkError =
        error instanceof Error && isChunkLoadError(error) ? error : getLastChunkLoadError();
      if (chunkError) {
        void recoverFromChunkLoadError(chunkError);
        return;
      }
      window.location.reload();
    };
    const showAsError = Boolean(error);
    const showStatusPageLink = showAsError && kind === 'server';

    const handleOpenStatusPage = () => {
      track(ANALYTICS_EVENTS.STATUS_PAGE.SPLASH_LINK_OPENED);
      if (window.desktopAPI) {
        window.desktopAPI.openUrl.openExternal.callback(STATUS_PAGE_PUBLIC_URL);
        return;
      }
      window.open(STATUS_PAGE_PUBLIC_URL, '_blank', 'noopener,noreferrer');
    };

    useEffect(() => {
      if (timeoutMessageRef.current && !error && !noTimeout) {
        timeoutMessageRef.current.animate([{ opacity: 0 }, { opacity: 1 }], {
          duration: 500,
          delay: LOADING_DURATION_MS,
          easing: 'ease-in-out',
          fill: 'forwards',
        });
      }
    }, [error, noTimeout]);

    // Report diagnostics once when the splash enters error mode. Skipped
    // while a chunk-load recovery reload is in flight: the boundary error in
    // that window is a transient secondary failure, and reporting it would
    // pollute the chunk-failure signal right before the page reloads anyway.
    const reportedRef = useRef(false);
    useEffect(() => {
      if (!showAsError || reportedRef.current || isChunkLoadRecoveryPending()) {
        return;
      }
      reportedRef.current = true;
      reportSplashError(error, { bootedInError });
    }, [showAsError, error, bootedInError]);

    // Bar fill values. Pre-mount, the loading bar starts at 0%; once mounted,
    // it animates to 100% over LOADING_DURATION_MS (or jumps to 90% danger if
    // we boot directly into error). Width transition duration depends on which
    // direction we're moving — slow for the loading sweep, quick for the
    // failure snap.
    const fillWidth = showAsError ? '90%' : hasMounted ? '100%' : '0%';
    const errorGradient = kind === 'client' ? WARNING_GRADIENT : DANGER_GRADIENT;
    const fillBackground = showAsError ? errorGradient : HIGHLIGHT_GRADIENT;
    const fillTransition = showAsError
      ? 'width 500ms cubic-bezier(0.4, 0, 0.2, 1), background 500ms ease'
      : `width ${LOADING_DURATION_MS}ms cubic-bezier(0.4, 0, 0.2, 1), background 500ms ease`;

    return (
      <div className="relative size-full overflow-hidden bg-primary-3">
        {/* Diffuse yellow-green wash at the bottom of the canvas — same asset
          used on the Power Dialer empty state. Sits behind the content. */}
        <img
          src={gradientBackdrop}
          alt=""
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 bottom-0 z-0 w-full object-cover"
        />
        <div className="flex size-full flex-col items-center">
          {/* Logo pinned at the bottom; the active block (welcome+bar or
            error+retry) sits centered in the remaining space. pt-8
            compensates the logo's bottom offset so the block reads optically
            centered, not geometrically. */}
          <div className="flex w-full flex-1 flex-col items-center justify-center gap-6 pt-8">
            {/* Text block: greeting and error copy are crossfaded in the same
              slot. Both are mounted simultaneously and positioned absolute so
              the layout doesn't jump as opacity transitions. The wrapper
              reserves the larger height (error block = title + subtitle) so
              the bar below stays anchored. */}
            <div className="relative flex w-2/3 max-w-xs justify-center md:w-1/3">
              {/* Layout spacer: invisible copy of the error block reserves the
                vertical space so neither state shifts the bar below. */}
              <div className="invisible flex flex-col items-center gap-1.5" aria-hidden="true">
                <p className="w-full text-center text-heading-16">
                  {t('splashscreen.error.title')}
                </p>
                <p className="w-full text-center text-body-13">
                  {t('splashscreen.error.description')}
                </p>
              </div>

              <div
                aria-hidden={showAsError}
                className={cn(
                  'absolute inset-0 flex flex-col items-center justify-center transition-opacity duration-500',
                  error ? 'pointer-events-none opacity-0' : 'opacity-100',
                )}
              >
                {!bootedInError && <Greeting userName={userName} />}
              </div>

              <div
                aria-hidden={!showAsError}
                className={cn(
                  'absolute inset-0 flex flex-col items-center gap-1.5 transition-opacity duration-500',
                  error ? 'opacity-100' : 'pointer-events-none opacity-0',
                )}
              >
                <p className="w-full text-balance text-center text-heading-16 text-primary-100">
                  {t(
                    kind === 'client'
                      ? 'splashscreen.error.clientTitle'
                      : 'splashscreen.error.title',
                  )}
                </p>
                <p className="w-full text-pretty text-center text-body-13 text-primary-70">
                  {(() => {
                    if (kind === 'client') {
                      return t('splashscreen.error.clientDescription');
                    }
                    if (error instanceof NoVonageTokenError) {
                      return t('splashscreen.error.vonage.token');
                    }
                    if (error instanceof NoVonageSessionError) {
                      return t('splashscreen.error.vonage.session');
                    }
                    return t('splashscreen.error.description');
                  })()}
                </p>
              </div>
            </div>

            {/* Bar: single fill div whose width and background transition
              smoothly. Track tint follows the kind variant outside the
              loading state (warning-light for client, danger-light for
              server). */}
            <div className="w-2/3 max-w-xs md:w-1/3">
              <div
                className={cn(
                  'relative h-2 w-full overflow-hidden rounded-sm transition-colors duration-500',
                  showAsError
                    ? kind === 'client'
                      ? 'bg-status-warning-light'
                      : 'bg-status-danger-light'
                    : 'bg-primary-10',
                )}
              >
                <div
                  className="h-full rounded-sm"
                  style={{
                    width: fillWidth,
                    background: fillBackground,
                    transition: fillTransition,
                  }}
                />
              </div>
            </div>

            {/* Retry button: animates in when the splash drops into error mode.
              Reserved slot prevents the surrounding block from shifting up
              when the button appears. */}
            <div className="h-9">
              {showAsError && (
                <Button
                  className="animate-in fade-in slide-in-from-bottom-1 duration-300"
                  variant="secondary"
                  onClick={handleRetry}
                >
                  {t('splashscreen.error.button.retry')}
                </Button>
              )}
            </div>
          </div>

          <div className="pb-20">
            <AlloLogo className="h-8" />
          </div>
        </div>

        {/* Slow-connection helper: fades in 10s into a non-error load. Sits
          above the logo so it never overlaps. Width-constrained to mirror
          the central content, typography harmonized with the rest of the
          screen (text-body-13 / primary-60, text-pretty for orphan
          control). */}
        {!error && !noTimeout && (
          <p
            ref={timeoutMessageRef}
            className="absolute right-0 bottom-40 left-0 mx-auto max-w-sm px-8 text-pretty text-center text-body-13 text-primary-60"
            style={{ opacity: 0 }}
          >
            {t('splashscreen.timeout')}
          </p>
        )}

        {/* Status-page link on the server-error variant only. Same visual
            slot as the slow-connection helper (text-body-13 / primary-60,
            absolute bottom-40), rendered as a rounded-xl pill with a quiet
            hover/active bg. The client variant intentionally omits this —
            pointing the user at a public status page on a JS render bug
            would mislead. */}
        {showStatusPageLink && (
          <button
            type="button"
            onClick={handleOpenStatusPage}
            className="absolute right-0 bottom-40 left-0 mx-auto inline-flex w-fit max-w-sm cursor-pointer items-center gap-1.5 rounded-xl px-3 py-1.5 text-body-13 text-primary-60 transition-colors duration-200 ease-out hover:bg-primary-alpha-5 hover:text-primary-100 active:bg-primary-alpha-10"
          >
            <span>{t('splashscreen.error.statusPageLink')}</span>
            <Icon name={Icons.ArrowSquareOut} className="text-s" />
          </button>
        )}
      </div>
    );
  },
);
